import { useState, useEffect } from 'react'
import { supabase } from '../api/supabase'
import { fetchAllRows } from '../lib/fetchAllRows'

export interface CustomerOption {
  id: string
  code: string
  name: string
}

// Z88: one session-wide cache per role -- the list is needed by ~10 screens and rarely changes.
// Cleared by clearCustomersByRoleCache() after something creates a customer.
const cache = new Map<string, Promise<CustomerOption[]>>()
export function clearCustomersByRoleCache() { cache.clear() }

function loadByRole(roleCode: string): Promise<CustomerOption[]> {
  let p = cache.get(roleCode)
  if (!p) {
    // Single inner-join query, paged. The old version fetched every customer_id with the role and
    // pasted them into .in('id', ids): past a few hundred ids the URL is too long and the request
    // fails, and both reads were silently capped at 1000 rows.
    p = fetchAllRows<{ id: string; code: string; name: string }>((from, to) =>
      supabase
        .from('customers')
        .select('id, code, name, customer_address_types!inner(address_types!inner(code))')
        .eq('is_active', true)
        .eq('customer_address_types.address_types.code', roleCode)
        .order('name').order('id')
        .range(from, to) as never,
    ).then(rows => rows.map(r => ({ id: r.id, code: r.code, name: r.name })))
    p.catch(() => cache.delete(roleCode))
    cache.set(roleCode, p)
  }
  return p
}

/**
 * Returns customers that have a specific address_type role assigned via the
 * customer_address_types junction table. Defaults to 'Cus' (the
 * Customer/Client role — the only entities eligible for invoicing).
 *
 * Loads the full role list (paged, cached). For a picker prefer CustomerCombobox, which searches
 * server-side and never holds the whole list.
 */
export function useCustomersByRole(roleCode = 'Cus'): {
  customers: CustomerOption[]
  loading: boolean
} {
  const [customers, setCustomers] = useState<CustomerOption[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let cancelled = false
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setLoading(true)
    loadByRole(roleCode)
      .then(rows => { if (!cancelled) setCustomers(rows) })
      .catch(err => { console.error('[useCustomersByRole] failed:', err); if (!cancelled) setCustomers([]) })
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [roleCode])

  return { customers, loading }
}
